import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon, 
  MapPinIcon, 
  UserGroupIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import { Mission, Priority, MissionStatus } from '../types';
import { formatMissionType, formatMissionStatus, formatTimeAgo } from '../utils/formatUtils';
import clsx from 'clsx';

interface MissionCardProps {
  mission: Mission;
  onClick?: () => void;
}

const MissionCard: React.FC<MissionCardProps> = ({ mission, onClick }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/mission/${mission.id}`);
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.CRITICAL:
        return 'border-red-500 bg-red-50';
      case Priority.URGENT:
        return 'border-yellow-500 bg-yellow-50';
      case Priority.ROUTINE:
        return 'border-green-500 bg-green-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const getStatusColor = (status: MissionStatus) => {
    switch (status) {
      case MissionStatus.RECEIVED:
      case MissionStatus.ASSIGNED:
        return 'bg-gray-100 text-gray-800';
      case MissionStatus.AIRCRAFT_NOTIFIED:
      case MissionStatus.AIRCRAFT_LAUNCHED:
      case MissionStatus.AIRCRAFT_AT_CREW_BASE:
      case MissionStatus.CREW_ABOARD:
        return 'bg-blue-100 text-blue-800';
      case MissionStatus.ENROUTE_TO_SCENE:
      case MissionStatus.ON_SCENE:
      case MissionStatus.PATIENT_CONTACT:
        return 'bg-purple-100 text-purple-800';
      case MissionStatus.TRANSPORTING:
        return 'bg-green-100 text-green-800';
      case MissionStatus.AT_DESTINATION:
      case MissionStatus.MISSION_COMPLETE:
        return 'bg-emerald-100 text-emerald-800';
      case MissionStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return formatMissionStatus(status);
  };

  const getElapsedTime = () => {
    if (!mission.timeReceived) return null;
    const start = new Date(mission.timeReceived);

    // For closed missions, calculate against dateClosed/timeMissionComplete instead of now
    const closedStatuses = [MissionStatus.CLOSED, MissionStatus.MISSION_COMPLETE, MissionStatus.CANCELLED];
    const isClosed = closedStatuses.includes(mission.status as MissionStatus);
    let end = new Date();
    if (isClosed) {
      const closedTime = mission.dateClosed || mission.timeMissionComplete;
      if (closedTime) end = new Date(closedTime);
    }

    const diff = end.getTime() - start.getTime();
    if (diff < 0) return null;

    const totalDays = diff / (1000 * 60 * 60 * 24);

    // If > 365 days, show date range
    if (totalDays > 365) {
      const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${startStr} - ${endStr}`;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    // If >= 24 hours, show Xd Xh Xm format
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}h ${minutes}m`;
    }

    return `${hours}h ${minutes}m`;
  };

  return (
    <div
      onClick={handleClick}
      className={clsx(
        'card p-4 cursor-pointer hover:shadow-md transition-shadow',
        'border-l-4',
        getPriorityColor(mission.priority as Priority),
        mission.isTestTraining && 'opacity-75'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-lg">
              {mission.missionNumber}
            </h3>
            {mission.isTestTraining && (
              <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                TEST/TRAINING
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className={clsx(
              'status-indicator',
              getStatusColor(mission.status as MissionStatus)
            )}>
              {formatStatus(mission.status)}
            </span>
            {getElapsedTime() && (
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {getElapsedTime()}
              </span>
            )}
          </div>
        </div>
        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
      </div>

      {/* Mission Type & Priority */}
      <div className="flex items-center gap-4 mb-3 text-sm">
        <span className="font-medium">
          {formatMissionType(mission.missionType)}
        </span>
        {mission.priority === Priority.CRITICAL && (
          <span className="flex items-center gap-1 text-red-600">
            <ExclamationTriangleIcon className="h-4 w-4" />
            CRITICAL
          </span>
        )}
      </div>

      {/* Primary Base */}
      {mission.primaryBase && (
        <div className="flex items-center gap-2 mb-2">
          <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">
            {mission.primaryBase.name} ({mission.primaryBase.code})
          </span>
        </div>
      )}

      {/* Location */}
      {mission.sceneLocation && (
        <div className="flex items-start gap-2 mb-2">
          <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5" />
          <span className="text-sm text-gray-700 line-clamp-2">
            {mission.sceneLocation}
          </span>
        </div>
      )}

      {/* Caller Info */}
      {mission.callerAgency && (
        <div className="flex items-center gap-2 mb-2">
          <PhoneIcon className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-700">
            {mission.callerAgency}
            {mission.callerName && ` - ${mission.callerName}`}
          </span>
        </div>
      )}

      {/* Patient Count & Aircraft */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t">
        <div className="flex items-center gap-1 text-sm text-gray-600">
          <UserGroupIcon className="h-4 w-4" />
          <span>{mission.patientCount || 0} patient{mission.patientCount !== 1 ? 's' : ''}</span>
        </div>
        {mission.aircraft && mission.aircraft.length > 0 && (
          <div className="text-sm text-gray-600">
            {mission.aircraft.length} aircraft assigned
          </div>
        )}
      </div>

      {/* Time Received */}
      <div className="text-xs text-gray-500 mt-2">
        Received: {formatTimeAgo(mission.timeReceived)}
      </div>
    </div>
  );
};

export default MissionCard;